/**
 * Challenges Page - Gamified money-saving challenges
 * Features:
 * - Browse & start challenges
 * - Track daily progress
 * - Compete with friends
 * - Share progress on social
 * - Earn badges
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiGet, apiPost } from '@/utils/api';
import { toast } from 'sonner';
import confetti from 'canvas-confetti';

// Challenge category colors
const CATEGORY_COLORS = {
  dining: { bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-300', gradient: 'from-orange-500 to-red-500' },
  savings: { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-300', gradient: 'from-green-500 to-emerald-500' },
  shopping: { bg: 'bg-pink-100', text: 'text-pink-700', border: 'border-pink-300', gradient: 'from-pink-500 to-rose-500' },
  coffee: { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-300', gradient: 'from-amber-500 to-yellow-500' },
  entertainment: { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-300', gradient: 'from-blue-500 to-indigo-500' }
};

// Difficulty badges
const DIFFICULTY_BADGES = {
  easy: { label: 'Easy', color: 'bg-green-100 text-green-700' },
  medium: { label: 'Medium', color: 'bg-yellow-100 text-yellow-700' },
  hard: { label: 'Hard', color: 'bg-red-100 text-red-700' }
};

// Challenge Card Component
function ChallengeCard({ template, onStart, isActive }) {
  const colors = CATEGORY_COLORS[template.category] || CATEGORY_COLORS.savings;
  const difficulty = DIFFICULTY_BADGES[template.difficulty] || DIFFICULTY_BADGES.easy;

  return (
    <div className={`rounded-2xl border-2 ${colors.border} overflow-hidden bg-white hover:shadow-lg transition-all`}>
      {/* Header */}
      <div className={`p-4 ${colors.bg}`}>
        <div className="flex items-center justify-between mb-2">
          <span className="text-4xl">{template.icon}</span>
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${difficulty.color}`}>
            {difficulty.label}
          </span>
        </div>
        <h3 className="text-lg font-bold text-gray-900">{template.name}</h3>
        <p className="text-sm text-gray-600">{template.description}</p>
      </div>

      {/* Body */}
      <div className="p-4">
        <div className="flex items-center gap-2 mb-3 text-sm text-gray-500">
          <span>📅</span>
          <span>{template.duration_days} days</span>
        </div>

        {/* Rules Preview */}
        <div className="mb-4">
          <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Rules</p>
          <ul className="space-y-1">
            {template.rules.slice(0, 2).map((rule, i) => (
              <li key={i} className="text-sm text-gray-600 flex items-start gap-2">
                <span className="text-green-500">✓</span>
                {rule}
              </li>
            ))}
          </ul>
        </div>

        {/* Start Button */}
        <button
          onClick={() => onStart(template)}
          disabled={isActive}
          className={`w-full py-3 rounded-xl font-bold transition-all ${
            isActive
              ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
              : `bg-gradient-to-r ${colors.gradient} text-white hover:shadow-lg`
          }`}
          data-testid={`start-challenge-${template.id}`}
        >
          {isActive ? 'Already Active' : 'Start Challenge'}
        </button>
      </div>
    </div>
  );
}

// Active Challenge Card
function ActiveChallengeCard({ challenge, onCheckIn, onShare, onViewDetails }) {
  const colors = CATEGORY_COLORS[challenge.category] || CATEGORY_COLORS.savings;
  const progressPercent = challenge.progress_percent || 0;

  return (
    <div className="rounded-2xl bg-white shadow-lg overflow-hidden border border-gray-200">
      {/* Progress Header */}
      <div className={`p-4 bg-gradient-to-r ${colors.gradient} text-white`}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <span className="text-3xl">{challenge.icon}</span>
            <div>
              <h3 className="font-bold text-lg">{challenge.name}</h3>
              <p className="text-white/80 text-sm">Day {challenge.current_day} of {challenge.duration_days}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-2xl font-black">{Math.round(progressPercent)}%</p>
            <p className="text-white/80 text-xs">Complete</p>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="h-3 bg-white/30 rounded-full overflow-hidden">
          <div 
            className="h-full bg-white rounded-full transition-all duration-500"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </div>

      {/* Actions */}
      <div className="p-4">
        <div className="grid grid-cols-3 gap-2">
          <button
            onClick={() => onCheckIn(challenge)}
            className="py-3 bg-green-500 text-white rounded-xl font-medium hover:bg-green-600 transition-colors"
            data-testid={`checkin-${challenge.id}`}
          >
            ✅ Check In
          </button>
          <button
            onClick={() => onShare(challenge)}
            className="py-3 bg-white text-gray-700 rounded-xl font-medium border border-gray-300 hover:bg-gray-50 transition-colors"
          >
            📤 Share
          </button>
          <button
            onClick={() => onViewDetails(challenge)}
            className="py-3 bg-white text-gray-700 rounded-xl font-medium border border-gray-300 hover:bg-gray-50 transition-colors"
          >
            👥 View
          </button>
        </div>

        {/* Quick Stats */}
        <div className="mt-4 flex items-center justify-around text-center">
          <div>
            <p className="text-2xl font-bold text-green-600">${challenge.total_saved || 0}</p>
            <p className="text-xs text-gray-500">Saved</p>
          </div>
          <div className="h-8 w-px bg-gray-200" />
          <div>
            <p className="text-2xl font-bold text-blue-600">{challenge.participants?.length || 1}</p>
            <p className="text-xs text-gray-500">Participants</p>
          </div>
          <div className="h-8 w-px bg-gray-200" />
          <div>
            <p className="text-2xl font-bold text-orange-600">{challenge.streak || 0}</p>
            <p className="text-xs text-gray-500">Day Streak</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// Challenge Detail Modal with Leaderboard
function ChallengeDetailModal({ challenge, onClose, onInvite }) {
  const [inviteEmail, setInviteEmail] = useState('');
  const [copied, setCopied] = useState(false);

  const handleCopyLink = async () => {
    const link = `${window.location.origin}/challenges/join/${challenge.invite_code}`;
    await navigator.clipboard.writeText(link);
    setCopied(true);
    toast.success('Invite link copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleInvite = async () => {
    if (!inviteEmail) return;
    await onInvite(challenge.id, inviteEmail);
    setInviteEmail('');
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-4xl">{challenge.icon}</span>
              <div>
                <h2 className="text-xl font-bold text-gray-900">{challenge.name}</h2>
                <p className="text-sm text-gray-500">Day {challenge.current_day} of {challenge.duration_days}</p>
              </div>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl">
              ✕
            </button>
          </div>
        </div>

        {/* Leaderboard */}
        <div className="p-6">
          <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
            <span>🏆</span> Leaderboard
          </h3>
          
          <div className="space-y-3">
            {(challenge.participant_progress || []).map((participant, index) => (
              <div 
                key={participant.user_id}
                className={`flex items-center gap-3 p-3 rounded-xl ${
                  participant.is_current_user ? 'bg-blue-50 border-2 border-blue-200' : 'bg-gray-50'
                }`}
              >
                {/* Rank */}
                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                  index === 0 ? 'bg-yellow-400 text-yellow-900' :
                  index === 1 ? 'bg-gray-300 text-gray-700' :
                  index === 2 ? 'bg-orange-400 text-orange-900' :
                  'bg-gray-200 text-gray-600'
                }`}>
                  {index + 1}
                </div>

                {/* Name & Progress */}
                <div className="flex-1">
                  <p className="font-medium text-gray-900">
                    {participant.name}
                    {participant.is_current_user && <span className="text-blue-600 ml-1">(You)</span>}
                  </p>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-green-500 rounded-full"
                        style={{ width: `${participant.progress_percent}%` }}
                      />
                    </div>
                    <span className="text-xs text-gray-500">{participant.successful_days} days</span>
                  </div>
                </div>

                {/* Saved */}
                <div className="text-right">
                  <p className="font-bold text-green-600">${participant.total_saved}</p>
                  <p className="text-xs text-gray-500">saved</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Invite Section */}
        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <h3 className="font-bold text-gray-900 mb-3">Invite Friends</h3>
          
          {/* Invite Link */}
          <div className="flex gap-2 mb-3">
            <input
              type="text"
              readOnly
              value={`${window.location.origin}/challenges/join/${challenge.invite_code}`}
              className="flex-1 px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-600"
            />
            <button
              onClick={handleCopyLink}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                copied ? 'bg-green-500 text-white' : 'bg-gray-200 hover:bg-gray-300'
              }`}
            >
              {copied ? '✓ Copied' : 'Copy'}
            </button>
          </div>

          {/* Email Invite */}
          <div className="flex gap-2">
            <input
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="friend@email.com"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
            />
            <button
              onClick={handleInvite}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg font-medium hover:bg-blue-600"
            >
              Send Invite
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Check-in Modal
function CheckInModal({ challenge, onClose, onSubmit }) {
  const [amountSaved, setAmountSaved] = useState(challenge.category === 'savings' ? '5' : '0');
  const [note, setNote] = useState('');
  const [success, setSuccess] = useState(true);

  const handleSubmit = () => {
    onSubmit({
      amount_saved: parseFloat(amountSaved) || 0,
      note,
      success
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900">Daily Check-In</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl">
            ✕
          </button>
        </div>

        {/* Success Toggle */}
        <div className="mb-6">
          <p className="text-sm font-medium text-gray-700 mb-3">Did you stick to the challenge today?</p>
          <div className="flex gap-3">
            <button
              onClick={() => setSuccess(true)}
              className={`flex-1 py-3 rounded-xl font-medium border-2 transition-all ${
                success 
                  ? 'bg-green-500 text-white border-green-500' 
                  : 'bg-white text-gray-600 border-gray-200 hover:border-green-300'
              }`}
            >
              ✅ Yes!
            </button>
            <button
              onClick={() => setSuccess(false)}
              className={`flex-1 py-3 rounded-xl font-medium border-2 transition-all ${
                !success 
                  ? 'bg-red-500 text-white border-red-500' 
                  : 'bg-white text-gray-600 border-gray-200 hover:border-red-300'
              }`}
            >
              ❌ Not today
            </button>
          </div>
        </div>

        {/* Amount Saved */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Amount Saved ($)</label>
          <input
            type="number"
            value={amountSaved}
            onChange={(e) => setAmountSaved(e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl"
            placeholder="0"
          />
        </div>

        {/* Note */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-1">Note (optional)</label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl resize-none"
            rows={2}
            placeholder="How did today go?"
          />
        </div>

        <button
          onClick={handleSubmit}
          className="w-full py-4 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl font-bold hover:shadow-lg transition-all"
        >
          Complete Check-In
        </button>
      </div>
    </div>
  );
}

// Share Modal
function ShareModal({ shareData, onClose }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(shareData.share_text);
    setCopied(true);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShare = async (platform) => {
    const text = encodeURIComponent(shareData.share_text);
    const url = encodeURIComponent(`${window.location.origin}/challenges/join/${shareData.invite_code}`);
    
    let shareUrl;
    switch (platform) {
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?text=${text}&url=${url}`;
        break;
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?quote=${text}&u=${url}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900">Share Progress</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl">
            ✕
          </button>
        </div>

        {/* Progress Card Preview */}
        <div className="p-4 bg-gradient-to-r from-purple-500 to-indigo-500 rounded-xl text-white mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm opacity-80">My Progress</span>
            <span className="text-2xl font-black">{Math.round(shareData.progress_percent)}%</span>
          </div>
          <div className="h-2 bg-white/30 rounded-full overflow-hidden mb-2">
            <div 
              className="h-full bg-white rounded-full"
              style={{ width: `${shareData.progress_percent}%` }}
            />
          </div>
          <p className="text-sm">
            {shareData.days_complete} days complete • ${shareData.total_saved} saved
          </p>
        </div>

        {/* Share Text */}
        <div className="mb-6">
          <p className="text-sm font-medium text-gray-700 mb-2">Share Message</p>
          <div className="p-3 bg-gray-100 rounded-xl text-sm text-gray-700">
            {shareData.share_text}
          </div>
        </div>

        {/* Share Buttons */}
        <div className="flex gap-3">
          <button
            onClick={() => handleShare('twitter')}
            className="flex-1 py-3 bg-[#1DA1F2] text-white rounded-xl font-medium hover:bg-[#1a8cd8]"
          >
            Twitter
          </button>
          <button
            onClick={() => handleShare('facebook')}
            className="flex-1 py-3 bg-[#4267B2] text-white rounded-xl font-medium hover:bg-[#375695]"
          >
            Facebook
          </button>
          <button
            onClick={handleCopy}
            className={`flex-1 py-3 rounded-xl font-medium ${
              copied ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            {copied ? '✓ Copied' : 'Copy'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Main Challenges Page
export default function Challenges() {
  const navigate = useNavigate();
  const { inviteCode } = useParams();
  const [templates, setTemplates] = useState([]);
  const [activeChallenges, setActiveChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTemplateIds, setActiveTemplateIds] = useState([]);
  
  // Modal states
  const [showCheckIn, setShowCheckIn] = useState(null);
  const [showShare, setShowShare] = useState(null);
  const [showDetail, setShowDetail] = useState(null);

  useEffect(() => {
    loadData();
    // Handle invite code join
    if (inviteCode) {
      handleJoinChallenge(inviteCode);
    }
  }, [inviteCode]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [templatesRes, activeRes] = await Promise.all([
        apiGet('/challenges/templates'),
        apiGet('/challenges/active')
      ]);

      if (templatesRes.ok) {
        const data = await templatesRes.json();
        setTemplates(data.templates || []);
      }

      if (activeRes.ok) {
        const data = await activeRes.json();
        setActiveChallenges(data.challenges || []);
        setActiveTemplateIds(data.challenges?.map(c => c.template_id) || []);
      }
    } catch (error) {
      console.error('Error loading challenges:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStartChallenge = async (template) => {
    try {
      const response = await apiPost('/challenges/start', {
        template_id: template.id
      });

      if (response.ok) {
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 }
        });
        toast.success(`Started ${template.name}! Let's go! 🔥`);
        loadData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to start challenge');
      }
    } catch (error) {
      toast.error('Failed to start challenge');
    }
  };

  const handleCheckIn = async (data) => {
    if (!showCheckIn) return;

    try {
      const response = await apiPost(`/challenges/${showCheckIn.id}/check-in`, data);

      if (response.ok) {
        const result = await response.json();
        
        if (result.completed) {
          // Challenge completed!
          confetti({
            particleCount: 200,
            spread: 100,
            origin: { y: 0.6 }
          });
          toast.success('🎉 Challenge Complete! You earned a badge!');
          
          // Auto-complete the challenge
          await apiPost(`/challenges/${showCheckIn.id}/complete`);
        } else if (data.success) {
          confetti({
            particleCount: 50,
            spread: 50,
            origin: { y: 0.7 }
          });
          toast.success(`Day ${result.days_complete} complete! ${result.days_remaining} days to go!`);
        } else {
          toast.info("Tomorrow is a new day. Keep going! 💪");
        }
        
        setShowCheckIn(null);
        loadData();
      }
    } catch (error) {
      toast.error('Failed to check in');
    }
  };

  const handleShare = async (challenge) => {
    try {
      const response = await apiGet(`/challenges/${challenge.id}/share`);
      if (response.ok) {
        const data = await response.json();
        setShowShare(data);
      }
    } catch (error) {
      toast.error('Failed to load share data');
    }
  };

  const handleViewDetails = async (challenge) => {
    try {
      const response = await apiGet(`/challenges/${challenge.id}`);
      if (response.ok) {
        const data = await response.json();
        setShowDetail(data);
      }
    } catch (error) {
      toast.error('Failed to load challenge details');
    }
  };

  const handleInvite = async (challengeId, email) => {
    try {
      const response = await apiPost(`/challenges/${challengeId}/invite`, { email });
      if (response.ok) {
        toast.success(`Invite sent to ${email}!`);
      }
    } catch (error) {
      toast.error('Failed to send invite');
    }
  };

  const handleJoinChallenge = async (code) => {
    try {
      const response = await apiPost(`/challenges/join/${code}`);
      if (response.ok) {
        const data = await response.json();
        confetti({ particleCount: 100, spread: 70 });
        toast.success(data.message);
        loadData();
        navigate('/app/challenges');
      }
    } catch (error) {
      toast.error('Invalid or expired invite link');
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-4">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-64 bg-gray-200 rounded-2xl"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-4 pb-20">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-black text-gray-900">Money Challenges</h1>
        <p className="text-gray-500">Build better habits and compete with friends</p>
      </div>

      {/* Active Challenges */}
      {activeChallenges.length > 0 && (
        <div className="mb-8">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <span>🔥</span> Active Challenges
          </h2>
          <div className="space-y-4">
            {activeChallenges.map(challenge => (
              <ActiveChallengeCard
                key={challenge.id}
                challenge={challenge}
                onCheckIn={setShowCheckIn}
                onShare={handleShare}
                onViewDetails={handleViewDetails}
              />
            ))}
          </div>
        </div>
      )}

      {/* Available Challenges */}
      <div>
        <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <span>🎯</span> Available Challenges
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {templates.map(template => (
            <ChallengeCard
              key={template.id}
              template={template}
              onStart={handleStartChallenge}
              isActive={activeTemplateIds.includes(template.id)}
            />
          ))}
        </div>
      </div>

      {/* Modals */}
      {showCheckIn && (
        <CheckInModal
          challenge={showCheckIn}
          onClose={() => setShowCheckIn(null)}
          onSubmit={handleCheckIn}
        />
      )}

      {showShare && (
        <ShareModal
          shareData={showShare}
          onClose={() => setShowShare(null)}
        />
      )}

      {showDetail && (
        <ChallengeDetailModal
          challenge={showDetail}
          onClose={() => setShowDetail(null)}
          onInvite={handleInvite}
        />
      )}
    </div>
  );
}
